import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';
import './TaskBoard.css';
import { useAuth } from '../hooks/useAuth';
import { 
    FaPlus, FaUserFriends, FaRegCommentDots, FaCalendarAlt, 
    FaTrash, FaPencilAlt, FaCheck, FaTimes, FaSearch, FaClock,
    FaLayerGroup, FaGripVertical
} from 'react-icons/fa';

const KANBAN_BASE_PATH = '/api/tasks';

// --- Helpers Visuais ---
const getInitials = (name) => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
};

const getRandomColor = (id) => {
    const colors = ['#FF8C00', '#0F1B4D', '#0a9e6e', '#7c3aed', '#0891b2', '#be185d'];
    return colors[(id || 0) % colors.length];
};

const getDateStatus = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    if (date < today)  return { label: 'Atrasado', class: 'kb-status-overdue' };
    if (date.getTime() === today.getTime()) return { label: 'Hoje', class: 'kb-status-today' };
    return { label: 'No Prazo', class: 'kb-status-future' };
};

// ====================================================================
// MODAL DE COMPARTILHAMENTO
// ====================================================================
const UserShareModal = ({ allUsers, sharedUserIds, onSave, onClose }) => {
    const [selectedUsers, setSelectedUsers] = useState(new Set(sharedUserIds ?? []));
    const { id: currentUserId } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');

    // Guard: garante que sempre é array, independente do que a API retornar
    const safeAllUsers = Array.isArray(allUsers) ? allUsers : [];

    const handleToggleUser = (userId) => {
        const next = new Set(selectedUsers);
        next.has(userId) ? next.delete(userId) : next.add(userId);
        setSelectedUsers(next);
    };

    const handleSave = () => {
        onSave(Array.from(selectedUsers));
        onClose();
    };

    const usersToDisplay = safeAllUsers
        .filter(u => String(u.id) !== String(currentUserId))
        .filter(u => u.nome?.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="kb-modal-overlay">
            <div className="kb-modal-content kb-modal-sm">
                <div className="kb-modal-header">
                    <div className="kb-modal-title-wrapper">
                        <div className="kb-modal-icon-badge"><FaUserFriends /></div>
                        <h3>Compartilhar Tarefa</h3>
                    </div>
                    <button onClick={onClose} className="kb-btn-icon-close">&times;</button>
                </div>

                <div className="kb-search-wrapper-modal">
                    <FaSearch className="kb-search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar usuário..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="kb-modal-search"
                    />
                </div>

                <div className="kb-user-list-container">
                    {usersToDisplay.length > 0
                        ? usersToDisplay.map(user => (
                            <div
                                key={user.id}
                                className={`kb-user-share-item ${selectedUsers.has(user.id) ? 'selected' : ''}`}
                                onClick={() => handleToggleUser(user.id)}
                            >
                                <div className="kb-avatar-small" style={{ backgroundColor: getRandomColor(user.id) }}>
                                    {getInitials(user.nome)}
                                </div>
                                <span className="kb-user-name">{user.nome}</span>
                                <div className="kb-checkbox-custom">
                                    {selectedUsers.has(user.id) && <FaCheck />}
                                </div>
                            </div>
                          ))
                        : <p className="kb-empty-text">Nenhum usuário encontrado.</p>
                    }
                </div>

                <div className="kb-modal-footer">
                    <div />
                    <div className="kb-footer-right">
                        <button type="button" onClick={onClose} className="kb-btn kb-btn-secondary">Cancelar</button>
                        <button type="button" onClick={handleSave} className="kb-btn kb-btn-primary">
                            <FaCheck /> Confirmar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ====================================================================
// MODAL DE TAREFA (CRIAÇÃO / EDIÇÃO)
// ====================================================================
const TaskModal = ({ task, onClose, onSave, users, columns, onDelete }) => {
    const [formData, setFormData] = useState({
        titulo: '', descricao: '', data_inicio: '', data_finalizacao: '',
        usuarios_compartilhados: [], coluna_id: null, ...task
    });
    const [notas, setNotas] = useState([]);
    const [novaNota, setNovaNota] = useState('');
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const { id: currentUserId, nome: currentUserName, perfil } = useAuth();

    useEffect(() => {
        if (task?.id) {
            const fetchNotas = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const { data } = await api.get(
                        `${KANBAN_BASE_PATH}/tarefas/${task.id}/notas`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setNotas(data);
                } catch (err) { console.error('Erro notas:', err); }
            };
            fetchNotas();
        }
        if (!formData.coluna_id && columns.length > 0) {
            setFormData(prev => ({ ...prev, coluna_id: columns[0].id }));
        }
    }, [task, columns]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveSharedUsers = updatedIds => {
        setFormData(prev => ({ ...prev, usuarios_compartilhados: updatedIds }));
    };

    const handleSubmit = e => {
        e.preventDefault();
        onSave(formData);
    };

    const handleAddNota = async () => {
        if (!novaNota.trim() || !task?.id) return;
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await api.post(`${KANBAN_BASE_PATH}/tarefas/${task.id}/notas`, { nota: novaNota, autor: currentUserName }, config);
            const { data } = await api.get(`${KANBAN_BASE_PATH}/tarefas/${task.id}/notas`, config);
            setNotas(data);
            setNovaNota('');
        } catch (err) { console.error('Erro nota:', err); }
    };

    const canDelete = perfil === 'admin_geral' || (task?.criado_por === currentUserId);
    const safeUsers = Array.isArray(users) ? users : [];
    const availableToShare = safeUsers.filter(u => String(u.id) !== String(currentUserId));
    const sharedCount = formData.usuarios_compartilhados?.length ?? 0;

    return (
        <>
            <div className="kb-modal-overlay" onClick={onClose}>
                <div className="kb-modal-content kb-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="kb-modal-header">
                        <div className="kb-modal-title-wrapper">
                            <div className="kb-modal-icon-badge">
                                <FaPencilAlt />
                            </div>
                            <h3>{task?.id ? 'Editar Tarefa' : 'Nova Tarefa'}</h3>
                        </div>
                        <button onClick={onClose} className="kb-btn-icon-close">&times;</button>
                    </div>

                    <div className="kb-modal-body-scroll">
                        <form id="task-form-main" onSubmit={handleSubmit} className="kb-form-grid">
                            {/* Título */}
                            <div className="kb-form-group kb-full-width">
                                <label htmlFor="titulo">Título da Tarefa</label>
                                <input
                                    id="titulo"
                                    type="text"
                                    name="titulo"
                                    value={formData.titulo}
                                    onChange={handleChange}
                                    required
                                    placeholder="Ex: Verificar documentação do cliente..."
                                    className="kb-input-lg"
                                />
                            </div>

                            {/* Coluna */}
                            <div className="kb-form-group">
                                <label htmlFor="coluna_id">Status / Coluna</label>
                                <div className="kb-select-wrapper">
                                    <select id="coluna_id" name="coluna_id" value={formData.coluna_id || ''} onChange={handleChange} required>
                                        {columns.map(col => (
                                            <option key={col.id} value={col.id}>{col.titulo}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Compartilhamento */}
                            <div className="kb-form-group">
                                <label>Compartilhar com</label>
                                <button
                                    type="button"
                                    className="kb-btn-share"
                                    onClick={() => setIsShareModalOpen(true)}
                                >
                                    <FaUserFriends />
                                    <span>
                                        {sharedCount > 0
                                            ? `${sharedCount} ${sharedCount === 1 ? 'pessoa' : 'pessoas'} adicionada${sharedCount > 1 ? 's' : ''}`
                                            : 'Adicionar Pessoas'}
                                    </span>
                                </button>
                            </div>

                            {/* Datas */}
                            <div className="kb-form-group">
                                <label htmlFor="data_inicio">Data de Início</label>
                                <input
                                    id="data_inicio"
                                    type="date"
                                    name="data_inicio"
                                    value={formData.data_inicio ? formData.data_inicio.split('T')[0] : ''}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="kb-form-group">
                                <label htmlFor="data_finalizacao">Prazo Final</label>
                                <input
                                    id="data_finalizacao"
                                    type="date"
                                    name="data_finalizacao"
                                    value={formData.data_finalizacao ? formData.data_finalizacao.split('T')[0] : ''}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Descrição */}
                            <div className="kb-form-group kb-full-width">
                                <label htmlFor="descricao">Descrição Detalhada</label>
                                <textarea
                                    id="descricao"
                                    name="descricao"
                                    value={formData.descricao || ''}
                                    onChange={handleChange}
                                    placeholder="Descreva os detalhes desta atividade..."
                                />
                            </div>

                            {/* Notas */}
                            {task?.id && (
                                <div className="kb-notas-section kb-full-width">
                                    <div className="kb-divider">
                                        <FaRegCommentDots />
                                        <span>Histórico e Notas</span>
                                    </div>
                                    <div className="kb-notas-list">
                                        {notas.length > 0
                                            ? notas.map((nota, i) => (
                                                <div key={i} className="kb-nota-bubble">
                                                    <div className="kb-nota-avatar" style={{ backgroundColor: getRandomColor(i) }}>
                                                        {getInitials(nota.usuario_nome)}
                                                    </div>
                                                    <div className="kb-nota-content">
                                                        <div className="kb-nota-meta">
                                                            <strong>{nota.usuario_nome}</strong>
                                                            <small>{new Date(nota.data_criacao).toLocaleString('pt-BR')}</small>
                                                        </div>
                                                        <p>{nota.nota}</p>
                                                    </div>
                                                </div>
                                              ))
                                            : <div className="kb-empty-state-small">Nenhuma observação registrada.</div>
                                        }
                                    </div>
                                    <div className="kb-nova-nota-input">
                                        <input
                                            type="text"
                                            value={novaNota}
                                            onChange={e => setNovaNota(e.target.value)}
                                            placeholder="Escreva um comentário e pressione Enter..."
                                            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddNota())}
                                        />
                                        <button type="button" onClick={handleAddNota}>
                                            <FaCheck />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    <div className="kb-modal-footer">
                        <div className="kb-footer-left">
                            {task?.id && canDelete && (
                                <button type="button" className="kb-btn kb-btn-danger-ghost" onClick={() => onDelete(task.id)}>
                                    <FaTrash /> Excluir
                                </button>
                            )}
                        </div>
                        <div className="kb-footer-right">
                            <button type="button" className="kb-btn kb-btn-secondary" onClick={onClose}>
                                Cancelar
                            </button>
                            <button type="submit" className="kb-btn kb-btn-primary" form="task-form-main">
                                <FaCheck /> Salvar Tarefa
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {isShareModalOpen && (
                <UserShareModal
                    allUsers={safeUsers}
                    sharedUserIds={formData.usuarios_compartilhados ?? []}
                    onSave={handleSaveSharedUsers}
                    onClose={() => setIsShareModalOpen(false)}
                />
            )}
        </>
    );
};

// ====================================================================
// COMPONENTE PRINCIPAL: TaskBoard
// ====================================================================
export default function TaskBoard() {
    const [columns, setColumns]         = useState([]);
    const [tasks, setTasks]             = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [allUsers, setAllUsers]       = useState([]);
    const [isAddingColumn, setIsAddingColumn] = useState(false);
    const [newColumnTitle, setNewColumnTitle] = useState('');
    const [editingColumn, setEditingColumn]   = useState({ id: null, title: '' });
    const [globalSearch, setGlobalSearch]     = useState('');
    const [isDragging, setIsDragging]         = useState(false);

    // ✅ Fix: fallback para quando nome ainda não carregou
    const { nome, perfil } = useAuth();
    const displayName = nome || '';

    const fetchData = async () => {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            const [colsRes, tasksRes, usersRes] = await Promise.all([
                api.get(`${KANBAN_BASE_PATH}/kanban/colunas`, config),
                api.get(`${KANBAN_BASE_PATH}/tarefas`, config),
                // /selecao retorna array simples já filtrado pelo tenant do usuário logado
                api.get('/api/usuarios/selecao', config),
            ]);

            const toArray = (res) => {
                const d = res?.data;
                if (Array.isArray(d)) return d;
                if (d && Array.isArray(d.items))   return d.items;
                if (d && Array.isArray(d.data))    return d.data;
                if (d && Array.isArray(d.usuarios)) return d.usuarios;
                return [];
            };

            setColumns(toArray(colsRes));
            setTasks(toArray(tasksRes));
            setAllUsers(toArray(usersRes));
        } catch (err) { console.error('Erro load:', err); }
    };

    useEffect(() => { fetchData(); }, []);

    // Filtro local
    const filteredTasks = useMemo(() => {
        if (!globalSearch.trim()) return tasks;
        const lower = globalSearch.toLowerCase();
        return tasks.filter(t =>
            t.titulo.toLowerCase().includes(lower) ||
            (t.nome_criador && t.nome_criador.toLowerCase().includes(lower))
        );
    }, [tasks, globalSearch]);

    // ✨ Drag & Drop
    const onBeforeDragStart = () => setIsDragging(true);

    const onDragEnd = async (result) => {
        setIsDragging(false);
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const taskId    = parseInt(draggableId);
        const newColId  = parseInt(destination.droppableId);

        // Optimistic UI
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, coluna_id: newColId } : t));

        try {
            const token = localStorage.getItem('token');
            await api.put(
                `${KANBAN_BASE_PATH}/tarefas/${taskId}/status`,
                { coluna_id: newColId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            console.error('Erro mover:', err);
            fetchData(); // revert
        }
    };

    const handleOpenModal = (task = null, columnId = null) => {
        const initial = task ?? { coluna_id: columnId || (columns[0]?.id ?? null) };
        setEditingTask(initial);
        setIsModalOpen(true);
    };

    const handleSaveTask = async (taskData) => {
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        try {
            if (taskData.id) await api.put(`${KANBAN_BASE_PATH}/tarefas/${taskData.id}`, taskData, config);
            else             await api.post(`${KANBAN_BASE_PATH}/tarefas`, taskData, config);
            setIsModalOpen(false);
            setEditingTask(null);
            fetchData();
        } catch (err) { console.error('Erro salvar:', err); }
    };

    const handleDeleteTask = async (taskId) => {
        if (!window.confirm('Deseja remover esta tarefa permanentemente?')) return;
        const token = localStorage.getItem('token');
        try {
            await api.delete(`${KANBAN_BASE_PATH}/tarefas/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
            setIsModalOpen(false);
            setEditingTask(null);
            fetchData();
        } catch (err) { console.error('Erro deletar:', err); }
    };

    const handleAddNewColumn = async (e) => {
        e.preventDefault();
        if (!newColumnTitle.trim()) return;
        const token = localStorage.getItem('token');
        try {
            const { data: col } = await api.post(
                `${KANBAN_BASE_PATH}/kanban/colunas`,
                { titulo: newColumnTitle },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setColumns(prev => [...prev, col]);
            setNewColumnTitle('');
            setIsAddingColumn(false);
        } catch (err) { console.error('Erro coluna:', err); }
    };

    const handleRenameColumn = async (columnId) => {
        const original = columns.find(c => c.id === columnId)?.titulo;
        if (!editingColumn.title.trim() || editingColumn.title === original) {
            setEditingColumn({ id: null, title: '' });
            return;
        }
        const token = localStorage.getItem('token');
        try {
            await api.put(
                `${KANBAN_BASE_PATH}/kanban/colunas/${columnId}`,
                { titulo: editingColumn.title },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setColumns(prev => prev.map(c => c.id === columnId ? { ...c, titulo: editingColumn.title } : c));
            setEditingColumn({ id: null, title: '' });
        } catch (err) { console.error('Erro renomear:', err); }
    };

    const handleDeleteColumn = async (columnId) => {
        if (!window.confirm('Excluir esta coluna e todo seu conteúdo?')) return;
        const token = localStorage.getItem('token');
        try {
            await api.delete(`${KANBAN_BASE_PATH}/kanban/colunas/${columnId}`, { headers: { Authorization: `Bearer ${token}` } });
            setColumns(prev => prev.filter(c => c.id !== columnId));
            setTasks(prev => prev.filter(t => t.coluna_id !== columnId));
        } catch (err) { console.error('Erro excluir col:', err); }
    };

    // Greeting dinâmico por hora
    const getGreeting = () => {
        const h = new Date().getHours();
        if (h < 12) return 'Bom dia';
        if (h < 18) return 'Boa tarde';
        return 'Boa noite';
    };

    return (
        <div className="kb-board-container">
            {/* ─── HEADER ─────────────────────────────────────── */}
            <header className="kb-board-header">
                <div className="kb-header-title">
                    <div className="kb-header-title-icon">
                        <FaLayerGroup />
                    </div>
                    <div className="kb-header-title-text">
                        <h1>Gestão de Tarefas</h1>
                        {/* ✅ NOME CORRIGIDO com greeting dinâmico */}
                        <p>
                            {getGreeting()},{' '}
                            <strong>{displayName || 'usuário'}</strong>.
                            {' '}Organize suas prioridades.
                        </p>
                    </div>
                </div>

                <div className="kb-header-actions">
                    <div className="kb-search-bar">
                        <FaSearch />
                        <input
                            type="text"
                            placeholder="Filtrar tarefas..."
                            value={globalSearch}
                            onChange={e => setGlobalSearch(e.target.value)}
                        />
                    </div>
                </div>
            </header>

            {/* ─── CANVAS ─────────────────────────────────────── */}
            <div className="kb-canvas">
                <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragEnd}>
                    <div className="kb-columns-wrapper">
                        {columns.map(column => {
                            const colTasks = filteredTasks.filter(t => t.coluna_id === column.id);
                            return (
                                <Droppable key={column.id} droppableId={String(column.id)}>
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`kb-column${snapshot.isDraggingOver ? ' kb-dragging-over' : ''}`}
                                        >
                                            {/* Cabeçalho da coluna */}
                                            <div className="kb-column-header">
                                                {editingColumn.id === column.id ? (
                                                    <form
                                                        onSubmit={e => { e.preventDefault(); handleRenameColumn(column.id); }}
                                                        className="kb-rename-form"
                                                    >
                                                        <input
                                                            type="text"
                                                            value={editingColumn.title}
                                                            onChange={e => setEditingColumn({ ...editingColumn, title: e.target.value })}
                                                            onBlur={() => handleRenameColumn(column.id)}
                                                            autoFocus
                                                        />
                                                    </form>
                                                ) : (
                                                    <div
                                                        className="kb-column-title-group"
                                                        onClick={() => setEditingColumn({ id: column.id, title: column.titulo })}
                                                    >
                                                        <h3>{column.titulo}</h3>
                                                        <span className="kb-badge-count">{colTasks.length}</span>
                                                    </div>
                                                )}
                                                <div className="kb-column-options">
                                                    <button
                                                        onClick={() => handleDeleteColumn(column.id)}
                                                        className="kb-icon-btn delete"
                                                        title="Excluir coluna"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenModal(null, column.id)}
                                                        className="kb-icon-btn add"
                                                        title="Nova tarefa"
                                                    >
                                                        <FaPlus />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Lista de Cards */}
                                            <div className="kb-task-list">
                                                {colTasks.map((item, index) => {
                                                    const dateStatus = getDateStatus(item.data_finalizacao);
                                                    const sharedIds = item.usuarios_compartilhados ?? [];

                                                    return (
                                                        <Draggable
                                                            key={item.id}
                                                            draggableId={String(item.id)}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`kb-task-card${snapshot.isDragging ? ' kb-dragging' : ''}`}
                                                                    onClick={() => !snapshot.isDragging && handleOpenModal(item)}
                                                                    style={{
                                                                        ...provided.draggableProps.style,
                                                                        // Quando está sendo arrastado, reforça o estilo premium
                                                                        ...(snapshot.isDragging && {
                                                                            boxShadow: '0 24px 60px rgba(15,27,77,0.28), 0 0 0 2px #FF8C00',
                                                                            transform: `${provided.draggableProps.style?.transform || ''} rotate(2.5deg) scale(1.04)`,
                                                                        }),
                                                                    }}
                                                                >
                                                                    {/* Ícone de drag (visível no hover/drag) */}
                                                                    <div className="kb-drag-handle-hint">
                                                                        <FaGripVertical />
                                                                    </div>

                                                                    {dateStatus && (
                                                                        <div className={`kb-card-tag ${dateStatus.class}`}>
                                                                            {dateStatus.label}
                                                                        </div>
                                                                    )}

                                                                    <h4 className="kb-card-title">{item.titulo}</h4>

                                                                    <div className="kb-card-meta">
                                                                        <div className="kb-meta-left">
                                                                            {item.data_finalizacao && (
                                                                                <span className={`kb-date-info${dateStatus?.class === 'kb-status-overdue' ? ' kb-status-overdue-text' : ''}`}>
                                                                                    <FaClock />
                                                                                    {new Date(item.data_finalizacao).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="kb-meta-right">
                                                                            {sharedIds.length > 0 && (
                                                                                <div className="kb-shared-badge" title={`${sharedIds.length} pessoa(s) compartilhada(s)`}>
                                                                                    <FaUserFriends /> {sharedIds.length}
                                                                                </div>
                                                                            )}
                                                                            <div
                                                                                className="kb-avatar-mini"
                                                                                title={item.nome_criador}
                                                                                style={{ backgroundColor: getRandomColor(item.criado_por) }}
                                                                            >
                                                                                {getInitials(item.nome_criador)}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}
                                                {provided.placeholder}
                                            </div>
                                        </div>
                                    )}
                                </Droppable>
                            );
                        })}

                        {/* Botão de nova coluna */}
                        <div className="kb-add-column-wrapper">
                            {isAddingColumn ? (
                                <form onSubmit={handleAddNewColumn} className="kb-add-column-form">
                                    <input
                                        type="text"
                                        placeholder="Nome da lista..."
                                        value={newColumnTitle}
                                        onChange={e => setNewColumnTitle(e.target.value)}
                                        autoFocus
                                    />
                                    <div className="kb-form-actions-mini">
                                        <button type="submit" className="kb-btn-confirm"><FaCheck /></button>
                                        <button type="button" onClick={() => setIsAddingColumn(false)} className="kb-btn-cancel"><FaTimes /></button>
                                    </div>
                                </form>
                            ) : (
                                <button onClick={() => setIsAddingColumn(true)} className="kb-btn-add-column">
                                    <FaPlus /> Adicionar Lista
                                </button>
                            )}
                        </div>
                    </div>
                </DragDropContext>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <TaskModal
                    task={editingTask}
                    onClose={() => { setIsModalOpen(false); setEditingTask(null); }}
                    onSave={handleSaveTask}
                    users={allUsers}
                    columns={columns}
                    onDelete={handleDeleteTask}
                />
            )}
        </div>
    );
}